/**
 * INS-37 — a firm signing itself up on the inspector tenant app.
 *
 * Distinct from the Boer farm form (`POST /boer/signup`). This calls
 * `POST /inspect/signup` and does not whitelist the firm: unless the
 * programme says `requesterAccess === "any"` they still have to apply, and
 * the authority has to approve them before they can book. A missing
 * `requesterAccess` is whitelist.
 *
 * `not_found` ("Unknown application") means the flag is off, the app is not
 * live, or this host does not carry the inspector. The shell then falls back
 * to whatever it already shows an orgless visitor.
 */
import { ApiRequestError, apiRequest } from "./session";

export interface InspectSignupResult {
  outcome: "CREATED" | "EXISTING";
  organisationId: number | null;
  organisationName: string | null;
  needsChoice: boolean;
}

export type InspectSignupKind = "created" | "existing" | "choice";

export function hostsComplianceInspector(
  modules: readonly { pathPrefix?: string | null }[] | null | undefined,
): boolean {
  return !!modules?.some((m) => m.pathPrefix === "/compliance-inspector");
}

/** The flag-off response. 404 and Encore's `not_found` both count. */
export function isSignupUnavailable(error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    if (error.status === 404 || error.code === "not_found") return true;
    return error.message.toLowerCase().includes("unknown application");
  }
  return false;
}

export function classifyInspectSignup(result: InspectSignupResult): InspectSignupKind {
  if (result.needsChoice || result.organisationId == null) return "choice";
  return result.outcome === "CREATED" ? "created" : "existing";
}

export interface ProgrammeAccessRow {
  id: number;
  name: string;
  isActive: boolean;
  /** Missing means whitelist — the locked default. */
  requesterAccess: "whitelist" | "any";
}

/**
 * Where a newly created firm goes next.
 * `any` may book immediately. Anything else, including a missing field, applies
 * and waits for the authority.
 */
export function signupLanding(programmes: readonly ProgrammeAccessRow[]): {
  mode: "apply" | "book";
  programmeName: string;
  path: string;
} {
  const active = programmes.find((p) => p.isActive) ?? programmes[0];
  const mode = active?.requesterAccess === "any" ? "book" : "apply";
  const programmeName = active?.name?.trim() || "the inspection programme";
  const path =
    mode === "book"
      ? "/compliance-inspector?section=bookings&view=new"
      : "/compliance-inspector?section=bookings&view=apply";
  return { mode, programmeName, path };
}

export function parseProgrammeAccess(raw: unknown): ProgrammeAccessRow[] {
  const items = raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)
    ? (raw as { items: unknown[] }).items
    : [];
  const rows: ProgrammeAccessRow[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const id = Number(rec.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    rows.push({
      id,
      name: typeof rec.name === "string" ? rec.name : "",
      isActive: rec.isActive !== false,
      requesterAccess: rec.requesterAccess === "any" ? "any" : "whitelist",
    });
  }
  return rows;
}

export function parseInspectSignup(raw: unknown): InspectSignupResult | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const outcome = rec.outcome === "EXISTING" ? "EXISTING" : rec.outcome === "CREATED" ? "CREATED" : null;
  if (!outcome) return null;
  const organisationId = Number(rec.organisationId);
  return {
    outcome,
    organisationId: Number.isFinite(organisationId) && organisationId > 0 ? organisationId : null,
    organisationName: typeof rec.organisationName === "string" ? rec.organisationName : null,
    needsChoice: rec.needsChoice === true,
  };
}

export async function postInspectSignup(input: {
  authorization: string;
  tenantAppSlug: string;
  firmName: string;
  contactName?: string;
  contactPhone?: string;
}): Promise<InspectSignupResult> {
  const body: Record<string, string> = {
    authorization: input.authorization,
    tenantAppSlug: input.tenantAppSlug,
    firmName: input.firmName.trim(),
  };
  if (input.contactName?.trim()) body.contactName = input.contactName.trim();
  if (input.contactPhone?.trim()) body.contactPhone = input.contactPhone.trim();
  const raw = await apiRequest<unknown>("/inspect/signup", { method: "POST", body });
  const parsed = parseInspectSignup(raw);
  if (!parsed) throw new Error("Signup response was empty");
  return parsed;
}

export async function fetchProgrammeAccess(bearer: string, orgToken: string): Promise<ProgrammeAccessRow[]> {
  const raw = await apiRequest<unknown>("/inspect/programmes", { bearer, orgToken });
  return parseProgrammeAccess(raw);
}
